import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { NavigateFunction, useLocation } from 'react-router-dom'

import { CachedChapterEntry, ChapterContentCache } from './useChapterContentCache'
import { LexicalEditorRef } from '../../../components/editor/lexical'
import { ContentFile, SearchResult, apiService } from '../../../services/api'
import { ContentAST, findBlockById } from '../../../transforms/ast'
import { logger } from '../../../utils/logger'
import { buildEditorPassagePath, readEditorLinkTarget } from '../editorLinks'

interface ApplyLoadedChapterOptions {
  chapterAST?: ContentAST
  scrollBlockId?: string | null
  scrollHighlightText?: string | null
  scrollHighlightOffset?: number | null
  closeSidebar?: boolean
  /** Exact scroll offset to restore (cache-hit tab switch — content is identical, so pixels are exact). */
  scrollTop?: number | null
  /** Re-mark the editor as having unsaved changes (cache-restored file whose save hasn't landed). */
  restoreDirty?: boolean
}

interface UseChapterNavigationArgs {
  bookId?: string
  chapterId?: string
  chapters: ContentFile[]
  currentChapter: ContentFile | null
  currentContent: ContentAST | null
  isMobile: boolean
  navigate: NavigateFunction
  editorRef: MutableRefObject<LexicalEditorRef | null>
  createBookAST: () => ContentAST
  setMobileSidePaneOpen: (open: boolean) => void
  suppressSidebarAutoScrollTemporarily: (durationMs?: number) => void
  persistCurrentChapterBeforeNavigation: () => Promise<boolean>
  applyLoadedChapterToState: (chapter: ContentFile, options?: ApplyLoadedChapterOptions) => void
  /** Optional: returns the saved scroll-anchor blockId for a tab, used to restore scroll on switch. */
  getTabScrollAnchor?: (filename: string) => string | null
  /**
   * Optional: returns the exact last scroll offset for a tab. Preferred over the
   * heading anchor on cache hits (identical content); the anchor stays the fallback
   * for refetched content, whose layout may have changed.
   */
  getTabScrollTop?: (filename: string) => number | null
  /** Per-file content cache (author mode). A cached switch target is applied instantly, no refetch. */
  contentCache?: ChapterContentCache
  /** Receives the fresh server copy after the background revalidation of a cache-served file. */
  onChapterRevalidated?: (chapter: ContentFile) => void
}

const getSearchResultKey = (result: SearchResult) =>
  `content:${result.filename}:${result.blockId}:${result.blockMatchStart}:${result.annotationType ?? 'text'}:${(result.tagIds ?? []).slice().sort().join(',')}`

export function useChapterNavigation({
  bookId,
  chapterId,
  chapters,
  currentChapter,
  currentContent,
  isMobile,
  navigate,
  editorRef,
  createBookAST,
  setMobileSidePaneOpen,
  suppressSidebarAutoScrollTemporarily,
  persistCurrentChapterBeforeNavigation,
  applyLoadedChapterToState,
  getTabScrollAnchor,
  getTabScrollTop,
  contentCache,
  onChapterRevalidated,
}: UseChapterNavigationArgs) {
  const location = useLocation()
  // Blurs the still-visible stale page during ANY file→file switch (TOC click,
  // heading jump, search result, back/forward). searchChapterTransitioning is the
  // narrower flag that additionally shows the "Opening result..." pill.
  const [chapterTransitioning, setChapterTransitioning] = useState(false)
  const [searchChapterTransitioning, setSearchChapterTransitioning] = useState(false)
  const [tocChapterLoadingFilename, setTocChapterLoadingFilename] = useState<string | null>(null)
  const tocLoadingTargetRef = useRef<string | null>(null)
  const loadedChapterRef = useRef<string | null>(null)
  // Focus and visibilitychange both fire when a tab comes back; one check is enough.
  const revalidatingRef = useRef<string | null>(null)
  const pendingRouteChapterRef = useRef<string | null>(null)
  const basePath = '/edit'

  // Single shared entry point every file switch calls: blur the stale page until
  // the next file's content lands. endChapterTransition lifts it (on success via
  // applyLoadedChapter, or on a cancel/error path).
  const beginChapterTransition = useCallback(() => {
    setChapterTransitioning(true)
  }, [])
  const endChapterTransition = useCallback(() => {
    setChapterTransitioning(false)
    setSearchChapterTransitioning(false)
  }, [])

  const applyLoadedChapter = useCallback(
    (chapter: ContentFile, options?: ApplyLoadedChapterOptions) => {
      endChapterTransition()
      loadedChapterRef.current = chapter.filename
      if (tocLoadingTargetRef.current === chapter.filename) {
        tocLoadingTargetRef.current = null
        setTocChapterLoadingFilename(null)
      }
      applyLoadedChapterToState(chapter, options)
    },
    [applyLoadedChapterToState, endChapterTransition],
  )

  // Fires the outgoing file's save without blocking the switch. Must be called
  // before the incoming file is applied so the flush still targets the outgoing
  // editor. The cache entry stays dirty until the save succeeds, so a failure is
  // retried by autosave the next time that file is opened.
  const persistOutgoingInBackground = useCallback(() => {
    const outgoing = currentChapter?.filename
    if (!outgoing) {
      return
    }
    void persistCurrentChapterBeforeNavigation().then((ok) => {
      if (ok) {
        contentCache?.clearDirty(outgoing)
      }
    })
  }, [currentChapter?.filename, persistCurrentChapterBeforeNavigation, contentCache])

  /**
   * Freshness check for a cache-served file. Asks for the revision alone and
   * only downloads the document when it actually moved — an out-of-band write
   * (a content session editing the file on disk, another device). Without the
   * check the editor would keep serving its cached copy and the next autosave
   * would write it straight over whatever changed underneath.
   */
  const revalidateCachedChapter = useCallback(
    (filename: string, cachedRevision?: string) => {
      if (!bookId || !onChapterRevalidated || revalidatingRef.current === filename) {
        return
      }
      revalidatingRef.current = filename
      apiService
        .getChapterRevision(bookId, filename)
        .then((res) => {
          if (loadedChapterRef.current !== filename || !res.data?.revision) {
            return
          }
          if (res.data.revision === cachedRevision) {
            return
          }
          return apiService.getChapter(bookId, filename).then((fullChapter) => {
            if (loadedChapterRef.current === filename) {
              onChapterRevalidated(fullChapter.data)
            }
          })
        })
        .catch(() => {
          // Background freshness check only — the cached copy stays.
        })
        .finally(() => {
          if (revalidatingRef.current === filename) {
            revalidatingRef.current = null
          }
        })
    },
    [bookId, onChapterRevalidated],
  )

  const applyCachedChapter = useCallback(
    (entry: CachedChapterEntry, options?: ApplyLoadedChapterOptions) => {
      applyLoadedChapter(entry.chapter, {
        ...options,
        chapterAST: entry.ast,
        restoreDirty: entry.dirty,
      })
      revalidateCachedChapter(entry.chapter.filename, entry.chapter.revision)
    },
    [applyLoadedChapter, revalidateCachedChapter],
  )

  useEffect(() => {
    let cancelled = false

    const loadChapter = async () => {
      if (!bookId || !chapterId) {
        return
      }
      if (pendingRouteChapterRef.current && pendingRouteChapterRef.current !== chapterId) {
        return
      }
      if (pendingRouteChapterRef.current === chapterId) {
        pendingRouteChapterRef.current = null
      }
      if (loadedChapterRef.current === chapterId) {
        return
      }
      if (chapters.length === 0) {
        return
      }

      // An explicit URL target takes priority over scroll memory. A remembered
      // scroll offset is only trusted for cache-served content (identical
      // to what the user last saw); refetched content falls back to the heading anchor.
      const resolveScrollOptions = (fromCache: boolean): ApplyLoadedChapterOptions | undefined => {
        const target = readEditorLinkTarget(location)
        if (target) {
          return {
            scrollBlockId: target.blockId,
            scrollHighlightText: target.text,
            scrollHighlightOffset: target.offset,
          }
        }
        if (fromCache) {
          const savedScrollTop = getTabScrollTop?.(chapterId) ?? null
          if (savedScrollTop != null) {
            return { scrollTop: savedScrollTop }
          }
        }
        const tabAnchor = getTabScrollAnchor?.(chapterId) ?? null
        return tabAnchor ? { scrollBlockId: tabAnchor } : undefined
      }

      const cached = contentCache?.get(chapterId)
      if (cached) {
        applyCachedChapter(cached, resolveScrollOptions(true))
        return
      }

      beginChapterTransition()
      try {
        const fullChapter = await apiService.getChapter(bookId, chapterId)
        if (cancelled) {
          return
        }
        applyLoadedChapter(fullChapter.data, resolveScrollOptions(false))
      } catch (err) {
        if (cancelled) {
          return
        }
        endChapterTransition()
        logger.error('Failed to load chapter:', err)
      }
    }

    loadChapter()
    return () => {
      cancelled = true
    }
  }, [
    applyLoadedChapter,
    applyCachedChapter,
    beginChapterTransition,
    endChapterTransition,
    bookId,
    chapterId,
    chapters,
    contentCache,
    getTabScrollAnchor,
    getTabScrollTop,
    location.hash,
    location.search,
  ])

  const handleChapterSelect = useCallback(
    async (chapter: ContentFile) => {
      if (!bookId) {
        return
      }
      if (currentChapter?.filename === chapter.filename) {
        applyLoadedChapter(chapter)
        return
      }

      const cached = contentCache?.get(chapter.filename)
      if (cached) {
        // Instant switch: save the outgoing file in the background, apply the cached
        // copy, and revalidate it against the server (applyCachedChapter does that).
        persistOutgoingInBackground()
        pendingRouteChapterRef.current = chapter.filename
        const savedScrollTop = getTabScrollTop?.(chapter.filename) ?? null
        const tabAnchor = savedScrollTop == null ? (getTabScrollAnchor?.(chapter.filename) ?? null) : null
        applyCachedChapter(cached, { closeSidebar: true, scrollTop: savedScrollTop, scrollBlockId: tabAnchor })
        navigate(`${basePath}/${bookId}/${chapter.filename}`)
        return
      }

      try {
        tocLoadingTargetRef.current = chapter.filename
        setTocChapterLoadingFilename(chapter.filename)
        beginChapterTransition()
        if (currentChapter?.filename && currentChapter.filename !== chapter.filename) {
          const canSwitch = await persistCurrentChapterBeforeNavigation()
          if (!canSwitch) {
            endChapterTransition()
            if (tocLoadingTargetRef.current === chapter.filename) {
              tocLoadingTargetRef.current = null
              setTocChapterLoadingFilename(null)
            }
            return
          }
        }
        const fullChapter = await apiService.getChapter(bookId, chapter.filename)
        pendingRouteChapterRef.current = chapter.filename
        const tabAnchor = getTabScrollAnchor?.(chapter.filename) ?? null
        applyLoadedChapter(fullChapter.data, { closeSidebar: true, scrollBlockId: tabAnchor })
        navigate(`/edit/${bookId}/${chapter.filename}`)
      } catch (err) {
        endChapterTransition()
        pendingRouteChapterRef.current = null
        if (tocLoadingTargetRef.current === chapter.filename) {
          tocLoadingTargetRef.current = null
          setTocChapterLoadingFilename(null)
        }
        logger.error('Failed to load chapter:', err)
        toast.error('Failed to load chapter')
      }
    },
    [
      applyLoadedChapter,
      applyCachedChapter,
      beginChapterTransition,
      endChapterTransition,
      basePath,
      bookId,
      contentCache,
      currentChapter,
      navigate,
      persistCurrentChapterBeforeNavigation,
      persistOutgoingInBackground,
      getTabScrollAnchor,
      getTabScrollTop,
    ],
  )

  const handleHeadingNavigation = useCallback(
    async (chapter: ContentFile, headingBlockId: string) => {
      if (!bookId) {
        return
      }
      if (currentChapter?.filename !== chapter.filename) {
        const cached = contentCache?.get(chapter.filename)
        if (cached) {
          persistOutgoingInBackground()
          const headingBlock = findBlockById(cached.ast, headingBlockId)
          if (!headingBlock) {
            logger.warn(`Heading block "${headingBlockId}" not found in chapter`)
          }
          pendingRouteChapterRef.current = chapter.filename
          applyCachedChapter(cached, { scrollBlockId: headingBlock?.id || null })
          navigate(`${basePath}/${bookId}/${chapter.filename}#${headingBlockId}`)
          return
        }
        const canSwitch = await persistCurrentChapterBeforeNavigation()
        if (!canSwitch) {
          return
        }
        beginChapterTransition()
        try {
          const fullChapter = await apiService.getChapter(bookId, chapter.filename)
          const chapterAST: ContentAST = fullChapter.data.authorContent || createBookAST()

          const headingBlock = findBlockById(chapterAST, headingBlockId)
          pendingRouteChapterRef.current = chapter.filename
          applyLoadedChapter(fullChapter.data, {
            chapterAST,
            scrollBlockId: headingBlock?.id || null,
          })
          if (!headingBlock) {
            logger.warn(`Heading block "${headingBlockId}" not found in chapter`)
          }

          navigate(`${basePath}/${bookId}/${chapter.filename}#${headingBlockId}`)
        } catch (err) {
          endChapterTransition()
          pendingRouteChapterRef.current = null
          logger.error('Failed to load chapter:', err)
          toast.error('Failed to load chapter')
        }
      } else if (editorRef.current && currentContent) {
        const headingBlock = findBlockById(currentContent, headingBlockId)
        if (headingBlock) {
          editorRef.current.scrollToBlock(headingBlock.id)
          navigate(`${basePath}/${bookId}/${chapter.filename}#${headingBlockId}`)
        } else {
          logger.warn(`Heading block "${headingBlockId}" not found in current chapter`)
          editorRef.current.focus()
        }
      }
    },
    [
      applyLoadedChapter,
      applyCachedChapter,
      beginChapterTransition,
      endChapterTransition,
      basePath,
      bookId,
      contentCache,
      createBookAST,
      currentChapter?.filename,
      currentContent,
      editorRef,
      navigate,
      persistCurrentChapterBeforeNavigation,
      persistOutgoingInBackground,
    ],
  )

  const navigateToSearchResult = useCallback(
    async (filename: string, blockId: string, matchText: string, matchStart: number) => {
      if (!bookId) {
        return false
      }
      suppressSidebarAutoScrollTemporarily()

      if (isMobile) {
        setMobileSidePaneOpen(false)
      }

      const path = buildEditorPassagePath(bookId, filename, { blockId, text: matchText, offset: matchStart })
      if (currentChapter?.filename !== filename) {
        const canSwitch = await persistCurrentChapterBeforeNavigation()
        if (!canSwitch) {
          return false
        }
        beginChapterTransition()
        setSearchChapterTransitioning(true)
        pendingRouteChapterRef.current = filename
        loadedChapterRef.current = null
      }
      // The URL owns the complete target, so a search hit and a shared link behave
      // identically and a second block-only scroll cannot hide the match.
      navigate(path)
      return true
    },
    [
      beginChapterTransition,
      bookId,
      currentChapter?.filename,
      isMobile,
      navigate,
      persistCurrentChapterBeforeNavigation,
      setMobileSidePaneOpen,
      suppressSidebarAutoScrollTemporarily,
    ],
  )

  const handleSearchNavigate = useCallback(
    async (filename: string, blockId: string, matchText: string, matchStart: number) =>
      navigateToSearchResult(filename, blockId, matchText, matchStart),
    [navigateToSearchResult],
  )

  return {
    getSearchResultKey,
    chapterTransitioning,
    searchChapterTransitioning,
    tocChapterLoadingFilename,
    loadedChapterRef,
    revalidateCachedChapter,
    handleChapterSelect,
    handleHeadingNavigation,
    navigateToSearchResult,
    handleSearchNavigate,
  }
}
